import type {
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useStoreSpecials,
  useAddCartProduct as useV3AddCartProduct,
  useCreateCart as useV3CreateCart,
  useUpdateCartProduct as useV3UpdateCartProduct,
  useMaterializedMenuProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import type { PriceId } from '@iheartjane/types';
import { useCallback, useMemo } from 'react';

import type { CartRowProduct } from '@jane/kiosk/components/lib/ProductRow/cartRowProduct';
import { maxQuantityForProduct } from '@jane/kiosk/components/lib/ProductRow/cartRowProduct';
import {
  updateLocalCartUuid,
  useCreateCart,
  useUpdateCartProduct,
} from '@jane/kiosk/data-access';
import { useGetProductPrices } from '@jane/kiosk/hooks/lib/useGetProductPrices/useGetProductPrices';
import type { AddedToBagCart } from '@jane/kiosk/util';
import {
  useAddCartProduct,
  useGetStoreSpecials,
} from '@jane/shared-ecomm/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';
import {
  availableWeightsForMenuProduct,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

export interface AddProductToBagParams {
  cart?: { products?: CartRowProduct[]; uuid?: string };
  onSuccess: (newCart?: AddedToBagCart) => void;
  priceId?: PriceId;
  product: MaterializedMenuProduct;
  store: Store;
}

export const useAddProductToBag = ({
  cart,
  product: initialProduct,
  onSuccess,
  priceId,
  store,
}: AddProductToBagParams) => {
  const storeId = store.id;
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);

  const { data: product } = useMaterializedMenuProduct(
    {
      productId: initialProduct.product_id,
      storeId: storeId,
    },
    { initialData: initialProduct }
  );

  const getProductPrice = useGetProductPrices(product, specials);
  const weights = availableWeightsForMenuProduct(product);

  const { priceId: selectedWeight } = getProductPrice(
    priceId || lowestPriceForMenuProduct(product, weights) || 'each'
  );

  const { mutate: createCart, isPending: createLoading } = useCreateCart({
    onSuccess,
  });
  const { mutate: updateCartProduct, isPending: updateLoading } =
    useUpdateCartProduct({ onSuccess });
  const { mutate: addCartProduct, isPending: addLoading } = useAddCartProduct({
    onSuccess,
  });

  const { mutate: createCartV3, isPending: createLoadingV3 } = useV3CreateCart({
    onSuccess: (cart) => {
      updateLocalCartUuid(cart.uuid);
      onSuccess(cart);
    },
  });
  // v3 add/update return no Cart, so `() => onSuccess()` deliberately drops the
  // response arg to match v1 behavior (only create resolves with a Cart).
  const { mutate: updateCartProductV3, isPending: updateLoadingV3 } =
    useV3UpdateCartProduct({ onSuccess: () => onSuccess() });
  const { mutate: addCartProductV3, isPending: addLoadingV3 } =
    useV3AddCartProduct({ onSuccess: () => onSuccess() });

  const onAddToBag = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    async ({ quantity = 1, weight = selectedWeight }) => {
      const existingCartProduct = (cart?.products ?? []).find(
        ({ price_id, product_id }) =>
          product.product_id === product_id && price_id === weight
      );

      if (
        existingCartProduct &&
        existingCartProduct.count === maxQuantityForProduct(existingCartProduct)
      ) {
        return;
      }
      if (!cart?.uuid) {
        if (useV3Cart) {
          createCartV3({
            appMode: 'kiosk',
            reservationMode: 'kiosk',
            storeId,
            products: [
              {
                count: quantity,
                price_id: weight,
                product_id: product.product_id,
              },
            ],
          });
        } else {
          createCart({
            products: [
              {
                count: quantity,
                price_id: weight,
                product_id: product.product_id,
              },
            ],
            store_id: storeId,
          });
        }
      } else if (existingCartProduct) {
        if (useV3Cart) {
          updateCartProductV3({
            appMode: 'kiosk',
            cartUuid: cart.uuid,
            productId: product.product_id,
            count: existingCartProduct.count + quantity,
            priceId: weight,
          });
        } else {
          updateCartProduct({
            cart_uuid: cart.uuid,
            product: {
              count: existingCartProduct.count + quantity,
              price_id: weight,
              product_id: product.product_id,
            },
          });
        }
      } else if (useV3Cart) {
        addCartProductV3({
          appMode: 'kiosk',
          cartUuid: cart.uuid,
          storeId,
          product: {
            count: quantity,
            price_id: weight,
            product_id: product.product_id,
            recommended: false,
          },
        });
      } else {
        addCartProduct({
          app_mode: 'kiosk',
          app_mode_name: 'kiosk',
          cart_uuid: cart.uuid,
          product: {
            count: quantity,
            price_id: weight,
            product_id: product.product_id,
            recommended: false,
          },
          store_id: storeId,
          user_agent: window.navigator.userAgent,
        });
      }
    },
    [
      addCartProduct,
      addCartProductV3,
      cart?.products,
      cart?.uuid,
      createCart,
      createCartV3,
      product.product_id,
      selectedWeight,
      storeId,
      updateCartProduct,
      updateCartProductV3,
      useV3Cart,
    ]
  );
  return {
    isLoading: useV3Cart
      ? createLoadingV3 || updateLoadingV3 || addLoadingV3
      : createLoading || updateLoading || addLoading,
    onAddToBag,
  };
};
